import { CartUpdateEvent } from '@theme/events';

/* =========================================================
   UTILITY: Re-init event listeners for injected modal content
   After the modal body is replaced with fetched product HTML, wire variant
   inputs again so changes bubble to the theme and Update button labels stay in sync.
========================================================= */
function reinitModalFormEvents(modalContent) {
  // Variant inputs
  const variantInputs = modalContent.querySelectorAll('input[name="id"]');
  variantInputs.forEach((input) => {
    input.addEventListener('change', () => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(() => {
        // Keep add-to-cart control copy in “update” mode after the shopper changes options
        const updateButtonText = modalContent.querySelector('.add-to-cart-text__content');
        const updatedButtonText = modalContent.querySelector('.add-to-cart-text--added span:not(.svg-wrapper)');
        if (updateButtonText) updateButtonText.textContent = 'UPDATE';
        if (updatedButtonText) updatedButtonText.textContent = 'UPDATED';
      }, 100);
    });
  });
}

/** Root container for modal product markup (`#quick-update-modal-content`). */
function getQuickUpdateModalContent() {
  return document.getElementById('quick-update-modal-content');
}

/** Whether this form is inside the edit modal (exclude PDP / other add-to-cart forms). */
function isCartEditModalForm(form) {
  const modalContent = getQuickUpdateModalContent();
  return modalContent instanceof HTMLElement && modalContent.contains(form);
}

/** Fetches `/cart.js` for a full cart object after mutations. */
async function fetchCartJson() {
  const response = await fetch('/cart.js');
  if (!response.ok) throw new Error('Failed to load cart');
  return response.json();
}

/** True when `/cart/change.js` returned an HTTP error or Shopify `errors` in the JSON body. */
function isFailedCartChangeResponse(response, body) {
  if (!response.ok) return true;
  if (body && typeof body === 'object' && body.errors != null) return true;
  return false;
}

/** True when `/cart/add.js` returned an HTTP error or a Shopify error payload (`status` set). */
function isFailedCartAddResponse(response, body) {
  if (!response.ok) return true;
  if (body && typeof body === 'object' && body.status) return true;
  return false;
}

/* =========================================================
   UTILITY: Handle cart edit (add/change/remove)
   Updates the cart when the user confirms the edit modal: same variant = quantity change only;
   different variant = remove the old line then add the new one (with rollback if add fails).
========================================================= */
async function handleCartEdit(form) {
  const dialog = document.getElementById('quick-update-dialog');
  if (!dialog?.dataset.editing) return;

  const key = dialog.dataset.key;
  const originalVariantId = Number(dialog.dataset.originalVariantId);
  const cartQuantity = Number(dialog.dataset.quantity);
  const originalSellType = dialog.dataset.originalSellType || 'direct';

  const formData = new FormData(form);
  const newVariantId = Number(formData.get('id'));
  const quantity = Number(cartQuantity) || Number(formData.get('quantity') || 1);

  try {
    let cart;

    // ✅ Same variant → update quantity only
    if (originalVariantId === newVariantId) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const body = await response.json();
      if (isFailedCartChangeResponse(response, body)) {
        throw new Error(typeof body.errors === 'string' ? body.errors : 'Failed to update cart line');
      }
      cart = await fetchCartJson();
    }
    // ✅ Different variant → remove old line, then add new (remove first avoids duplicate lines)
    else {
      // 1️⃣ Remove old variant (must succeed before add so we never stack old + new)
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      const removeJson = await removeResponse.json();
      if (isFailedCartChangeResponse(removeResponse, removeJson)) {
        throw new Error(
          typeof removeJson.errors === 'string' ? removeJson.errors : 'Failed to remove previous variant from cart'
        );
      }

      formData.append('properties[_sell_type]', originalSellType);

      // 2️⃣ Add new variant
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const addJson = await addResponse.json();
      if (isFailedCartAddResponse(addResponse, addJson)) {
        // Best-effort rollback: restore previous variant if add fails after remove
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: originalVariantId, quantity }],
          }),
        }).catch(() => {});
        throw new Error(addJson.message || addJson.description || 'Failed to add new variant');
      }
      cart = await fetchCartJson();
    }

    // Dispatch Horizon / theme cart update event
    document.dispatchEvent(new CartUpdateEvent({}, 'cart-item-update', { cart, didError: false }));

    // Cleanup
    dialog.dataset.editing = '';
    dialog.closeDialog();
  } catch (error) {
    console.error('Cart update failed', error);
    document.dispatchEvent(new CartUpdateEvent({}, 'cart-item-update', { didError: true }));
  }
}

/**
 * Runs handleCartEdit for the cart-drawer modal form only, with an in-flight guard
 * so double submit / double click cannot run two updates at once.
 */
async function runCartEditIfModalForm(form) {
  const dialog = document.getElementById('quick-update-dialog');
  if (!dialog?.dataset.editing || !isCartEditModalForm(form)) return false;

  if (dialog.dataset.editInFlight === 'true') return true;
  dialog.dataset.editInFlight = 'true';
  try {
    await handleCartEdit(form);
  } finally {
    dialog.dataset.editInFlight = '';
  }
  return true;
}

/* =========================================================
   OPEN MODAL
   Loads the product section HTML into the quick-update dialog when the user
   clicks “Edit product” on a cart line, then preselects variant and quantity.
========================================================= */
document.addEventListener('click', async (e) => {
  const button = e.target.closest('.cart-edit-button');
  if (!button) return;

  const dialog = document.getElementById('quick-update-dialog');
  const modalContent = getQuickUpdateModalContent();
  if (!dialog || !modalContent) return;

  const productUrl = button.dataset.productUrl;
  const key = button.dataset.key;
  const variantId = button.dataset.variantId;
  const quantity = Number(button.dataset.quantity || 1);
  const updateButtonText = button.dataset.updateButtonText;
  const updatedButtonText = button.dataset.updatedButtonText;

  dialog.dataset.editing = 'true';
  dialog.dataset.editInFlight = '';
  dialog.dataset.key = key;
  dialog.dataset.originalVariantId = variantId;
  dialog.dataset.quantity = button.dataset.quantity;
  dialog.dataset.originalSellType = button.dataset.sellType || 'direct';
  dialog.dataset.updateButtonText = updateButtonText;
  dialog.dataset.updatedButtonText = updatedButtonText;

  try {
    const response = await fetch(productUrl);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');

    const productGrid = html.querySelector('[data-product-grid-content]');
    if (!productGrid) return;

    const clone = productGrid.cloneNode(true);
    modalContent.replaceChildren(...clone.childNodes);

    // Re-init all event listeners for the new DOM
    reinitModalFormEvents(modalContent);

    // Preselect variant & set quantity
    requestAnimationFrame(() => {
      const variantInput = modalContent.querySelector(`input[data-variant-id="${variantId}"]`);
      if (variantInput) {
        variantInput.checked = true;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const qtyInput = modalContent.querySelector('input[name="quantity"]');
      if (qtyInput) qtyInput.value = quantity;

      const updateButton = modalContent.querySelector('.add-to-cart-text__content');
      if (updateButton) updateButton.textContent = updateButtonText;

      const updatedButton = modalContent.querySelector('.add-to-cart-text--added span:not(.svg-wrapper)');
      if (updatedButton) updatedButton.textContent = updatedButtonText;
    });

    dialog.showDialog();
  } catch (error) {
    console.error('Failed loading edit modal', error);
  }
});

/* =========================================================
   INTERCEPT FORM SUBMIT (Enter key, etc.)
   Capture phase on document runs before product-form-component’s handleSubmit,
   so Enter in the modal cannot add a variant without removing the old line.
========================================================= */
document.addEventListener(
  'submit',
  async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    if (!isCartEditModalForm(form)) return;

    const dialog = document.getElementById('quick-update-dialog');
    if (!dialog?.dataset.editing) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    await runCartEditIfModalForm(form);
  },
  true // Capture phase: run before inner targets so we can cancel default submit
);

/* =========================================================
   INTERCEPT SUBMIT BUTTON (CAPTURE PHASE)
   Runs before other click handlers so the default add-to-cart path is replaced
   with handleCartEdit while the edit modal is open.
========================================================= */
document.addEventListener(
  'click',
  async (e) => {
    const dialog = document.getElementById('quick-update-dialog');
    if (!dialog?.dataset.editing) return;

    const submitBtn = e.target.closest('form[action*="/cart/add"] [type="submit"]');
    if (!submitBtn) return;

    const form = submitBtn.closest('form');
    if (!(form instanceof HTMLFormElement) || !isCartEditModalForm(form)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    await runCartEditIfModalForm(form);
  },
  true // Capture phase ensures we run before Horizon QuickAdd / product-form handlers
);
